import { useState, useCallback } from 'react';
import { useOnlineStatus } from '../lib/supabase/hooks/useOnlineStatus';

/**
 * Hook to track global sync status across the application
 * Returns:
 * - isOnline: boolean - network connectivity
 * - isSyncing: boolean - whether any sync operation is in progress
 * - hasPending: boolean - whether there are pending operations
 * - pendingCount: number - number of pending operations
 * - startSync: function - mark sync as started
 * - endSync: function - mark sync as ended
 * - addPending: function - add pending operation
 * - removePending: function - remove pending operation
 */
export function useSyncStatus() {
  const isOnline = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingOperations, setPendingOperations] = useState([]);

  // Track active sync operations
  const [, setActiveSyncs] = useState(0);

  const startSync = useCallback(() => {
    setActiveSyncs((prev) => prev + 1);
    setIsSyncing(true);
  }, []);

  const endSync = useCallback(() => {
    setActiveSyncs((prev) => {
      const newCount = Math.max(0, prev - 1);
      if (newCount === 0) {
        setIsSyncing(false);
      }
      return newCount;
    });
  }, []);

  const addPending = useCallback((operation) => {
    setPendingOperations((prev) => [...prev, operation]);
  }, []);

  const removePending = useCallback((operationId) => {
    setPendingOperations((prev) => prev.filter((op) => op.id !== operationId));
  }, []);

  const clearPending = useCallback(() => {
    setPendingOperations([]);
  }, []);

  const hasPending = pendingOperations.length > 0;
  const pendingCount = pendingOperations.length;

  return {
    isOnline,
    isSyncing,
    hasPending,
    pendingCount,
    pendingOperations,
    startSync,
    endSync,
    addPending,
    removePending,
    clearPending
  };
}

// Create a global sync status that can be shared across components
let globalSyncStatus = {
  isSyncing: false,
  pendingOperations: [],
  listeners: []
};

export const syncStatusManager = {
  startSync: () => {
    globalSyncStatus.isSyncing = true;
    globalSyncStatus.listeners.forEach((listener) => listener(globalSyncStatus));
  },

  endSync: () => {
    globalSyncStatus.isSyncing = false;
    globalSyncStatus.listeners.forEach((listener) => listener(globalSyncStatus));
  },

  addPending: (operation) => {
    globalSyncStatus.pendingOperations.push(operation);
    globalSyncStatus.listeners.forEach((listener) => listener(globalSyncStatus));
  },

  removePending: (operationId) => {
    globalSyncStatus.pendingOperations = globalSyncStatus.pendingOperations.filter(
      (op) => op.id !== operationId
    );
    globalSyncStatus.listeners.forEach((listener) => listener(globalSyncStatus));
  },

  subscribe: (listener) => {
    globalSyncStatus.listeners.push(listener);
    return () => {
      globalSyncStatus.listeners = globalSyncStatus.listeners.filter((l) => l !== listener);
    };
  },

  getStatus: () => globalSyncStatus
};
